import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, Medal, Crown, Flame } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const Leaderboard = () => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`/api/leaderboard`, { withCredentials: true });
        setLeaders(response.data);
      } catch (error) {
        console.error('Failed to fetch leaderboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 30000);
    return () => clearInterval(interval);
  }, []);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-300" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-sm font-bold text-muted-foreground w-6 text-center">{rank}</span>;
  };

  const getRankStyle = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'border-primary bg-primary/10 ring-1 ring-primary/30';
    if (rank === 1) return 'border-yellow-500/50 bg-yellow-900/10';
    if (rank === 2) return 'border-gray-400/30 bg-gray-900/10';
    if (rank === 3) return 'border-amber-600/30 bg-amber-900/10';
    return 'border-border';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-2 flex items-center gap-3">
            <Trophy className="w-8 h-8 text-primary" />
            Leaderboard
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">Who's putting in the work? Live rankings updated in real-time.</p>
        </div>

        {/* Top 3 Podium */}
        {leaders.length >= 3 && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            {[leaders[1], leaders[0], leaders[2]].map((leader, i) => {
              const podiumOrder = [2, 1, 3];
              const rank = podiumOrder[i];
              const heights = ['h-28', 'h-36', 'h-24'];
              const colors = ['bg-gray-400/20 border-gray-400/40', 'bg-yellow-500/20 border-yellow-500/40', 'bg-amber-600/20 border-amber-600/40'];
              return (
                <motion.div
                  key={leader.user_id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="flex flex-col items-center"
                  data-testid={`podium-${rank}`}
                >
                  <div className="w-12 h-12 rounded-full bg-zinc-800 border-2 border-zinc-600 flex items-center justify-center mb-2 overflow-hidden">
                    {leader.picture ? (
                      <img src={leader.picture} alt={leader.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-lg font-bold">{leader.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <p className="text-xs font-bold text-center truncate w-full mb-1">{leader.name}</p>
                  <p className="text-xs text-muted-foreground mb-2">{leader.workouts_completed} workouts</p>
                  <div className={`w-full ${heights[i]} ${colors[i]} border-2 rounded-t-sm flex items-start justify-center pt-2`}>
                    <span className="text-2xl font-extrabold">{rank}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Full Rankings */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Flame className="w-5 h-5 text-primary" />
              Full Rankings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {leaders.map((leader, index) => (
                <motion.div
                  key={leader.user_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  data-testid={`leaderboard-row-${leader.rank}`}
                  className={`flex items-center gap-4 px-4 py-3 transition-colors ${getRankStyle(leader.rank, leader.is_current_user)}`}
                >
                  <div className="flex-shrink-0 w-8 flex justify-center">
                    {getRankIcon(leader.rank)}
                  </div>
                  <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {leader.picture ? (
                      <img src={leader.picture} alt={leader.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold">{leader.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm truncate">
                        {leader.name}
                        {leader.is_current_user && <span className="text-primary ml-1">(You)</span>}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {leader.current_badge || 'Alpha Dad'} &middot; Level {leader.level || 1}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg font-extrabold text-primary">{leader.workouts_completed}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Workouts</p>
                  </div>
                </motion.div>
              ))}
              {leaders.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No workouts completed yet. Be the first!
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;
